import { computed, inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import {
  DashboardTile,
  DashboardTileDataset,
  DashboardTiles,
  MenuNode,
  MenuSetupNode,
} from '@qtek/shared/models';
import { getScopesFromMenuConfigByUrl } from '@qtek/shared/utils';
import {
  Observable,
  catchError,
  filter,
  mergeMap,
  of,
  pipe,
  switchMap,
  tap,
} from 'rxjs';
import { MenuService } from './menu.service';

interface MenuState {
  mainMenu: MenuNode[];
  setupMenu: MenuSetupNode[];
  dashboardMenu: DashboardTiles;
  datasets: Record<string, DashboardTileDataset[]>;
}

const initialState: MenuState = {
  mainMenu: [],
  setupMenu: [],
  dashboardMenu: null,
  datasets: {},
};

export const MenuStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),

  withMethods((store, menuService = inject(MenuService)) => {
    return {
      loadMain: rxMethod<void>(
        pipe(
          switchMap(() =>
            menuService.getMainMenu().pipe(
              tap(({ res }) => patchState(store, { mainMenu: res.children })),
              catchError(() => of(null))
            )
          )
        )
      ),

      loadSetup: rxMethod<void>(
        pipe(
          switchMap(() =>
            menuService.getSetupMenu().pipe(
              tap(({ res }) => patchState(store, { setupMenu: res.children })),
              catchError(() => of(null))
            )
          )
        )
      ),

      loadDashboard(): Observable<DashboardTiles> {
        return menuService.getDashboardMenu().pipe(
          tap(tiles => patchState(store, { dashboardMenu: tiles })),
          catchError(() => of(null))
        );
      },

      loadDataset: rxMethod<string>(
        pipe(
          filter(datasetType => !store.datasets()[datasetType]),
          mergeMap(datasetType =>
            menuService.getReportDataset(datasetType).pipe(
              tap(({ res }) => {
                const tiles = store.dashboardMenu();
                const labelledData: DashboardTileDataset[] = (
                  res?.data ?? []
                ).map((dataset: DashboardTileDataset) => {
                  const originalDataset = tiles?.tiles
                    ?.find(
                      (tile: DashboardTile) =>
                        tile.datasource?.type === datasetType
                    )
                    ?.datasets?.find(
                      (ds: DashboardTileDataset) => ds.id === dataset.id
                    );
                  return originalDataset
                    ? { ...originalDataset, ...dataset }
                    : dataset;
                });
                patchState(store, state => ({
                  datasets: { ...state.datasets, [datasetType]: labelledData },
                }));
              }),
              catchError(() => {
                // Cache an empty result so the tile falls through to its empty
                // state instead of spinning forever.
                patchState(store, state => ({
                  datasets: {
                    ...state.datasets,
                    [datasetType]: [] as DashboardTileDataset[],
                  },
                }));
                return of(null);
              })
            )
          )
        )
      ),

      pageScopesFromMenu(url: string) {
        return computed(() =>
          getScopesFromMenuConfigByUrl(store.mainMenu(), url)
        );
      },

      datasetByType(datasetType: string) {
        return computed(() => store.datasets()[datasetType]);
      },

      clearDataset(datasetType: string): void {
        patchState(store, state => {
          const { [datasetType]: _removed, ...rest } = state.datasets;
          return { datasets: rest };
        });
      },
    };
  })
);
